import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Checkbox, Chip, CircularProgress, Container, FormControlLabel, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import { FileDownload, InfoOutlined, Print, Receipt, Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import ReportSummaryCard, { summaryCardsRowSx } from "../components/Reports/ReportSummaryCard";
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from "../components/Pagination/StandardPagination";
import { useNavigation } from "../contexts/RouterContext";
import { filterFieldSx } from "../theme/filterStyles";
import { colors } from "../theme/designSystem";
import { maskName } from "../utils/nameMask";
import {
  packageSessionsService, PackageSessionRow, PackageSessionsFilters, PackageSessionsGroup, PackageSessionsReport,
} from "../services/packageSessionsService";

/**
 * Pacotes agendados (RN-224, item 193): as sessões de cada pacote, linha a linha, com o agendamento pai. Pacote é
 * serviço com mais de 1 sessão configurada; o agrupamento das sessões vindas da migração é reconstruído pelo
 * servidor, e a coluna Origem diz qual regra agrupou. O período escolhe pacotes, não sessões.
 */

const ORIGENS = ["Série", "Recibo", "Agendado junto", "Sequência"];
const SITUACOES = ["Completo", "Em andamento", "Incompleto", "Incompleto no início", "Excedente"];
const moeda = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const dataBr = (iso: string) => new Date(iso).toLocaleDateString("pt-BR");
const hora = (iso: string) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
const dataHoraBr = (iso: string) => `${dataBr(iso)} ${hora(iso)}`;

const situacaoCor: Record<string, { bg: string; fg: string }> = {
  Completo: { bg: colors.successSoft, fg: "#155724" },
  "Em andamento": { bg: "#e7f1ff", fg: "#0b4f8a" },
  Incompleto: { bg: colors.warningSoft, fg: colors.warningSoftText },
  "Incompleto no início": { bg: colors.warningSoft, fg: colors.warningSoftText },
  Excedente: { bg: "#fdecea", fg: "#8a1c1c" },
};

const FILTROS_INICIAIS: PackageSessionsFilters = {
  from: "", to: "", patient: "", professionalId: "", serviceTypeId: "", planKey: "", origin: "",
  packageSituation: "", paymentSituation: "", includeExcluded: true,
};

const Bloco: React.FC<{ titulo: string; grupos: PackageSessionsGroup[] }> = ({ titulo, grupos }) => (
  <Box sx={{ flex: "1 1 240px", minWidth: 0, border: "1px solid #e9ecef", borderRadius: "8px", p: 1.5, backgroundColor: "#fff" }}>
    <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>{titulo}</Typography>
    {grupos.length === 0 ? <Typography sx={{ fontSize: 12, color: "#adb5bd" }}>—</Typography> : grupos.map((g) => (
      <Box key={g.name} sx={{ display: "flex", justifyContent: "space-between", gap: 1, fontSize: 12.5, py: 0.25 }}>
        <span>{g.name}</span>
        <span style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{g.packages} pacotes · {g.sessions} sessões</span>
      </Box>
    ))}
  </Box>
);

const ReportPackageSessions: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();

  const [filtros, setFiltros] = useState<PackageSessionsFilters>(FILTROS_INICIAIS);
  const [busca, setBusca] = useState("");
  const [showNames, setShowNames] = useState(false);
  const [data, setData] = useState<PackageSessionsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(ITENS_POR_PAGINA_PADRAO);

  const muda = (campo: keyof PackageSessionsFilters, valor: string | boolean) => setFiltros((f) => ({ ...f, [campo]: valor }));

  // Busca por paciente com espera curta: evita uma consulta por tecla
  useEffect(() => {
    const t = setTimeout(() => setFiltros((f) => (f.patient === busca ? f : { ...f, patient: busca })), 400);
    return () => clearTimeout(t);
  }, [busca]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      setData(await packageSessionsService.get(filtros));
      setPagina(1);
    } catch {
      setErro("Não foi possível carregar o relatório.");
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);

  const linhas = useMemo(() => data?.items ?? [], [data]);
  const daPagina = useMemo(() => linhas.slice((pagina - 1) * porPagina, pagina * porPagina), [linhas, pagina, porPagina]);
  // Faixa alternada por pacote: as sessões do mesmo pai ficam visualmente juntas
  const faixa = useMemo(() => {
    const mapa = new Map<string, number>();
    linhas.forEach((l) => { if (!mapa.has(l.parentId)) mapa.set(l.parentId, mapa.size); });
    return mapa;
  }, [linhas]);
  const nome = (n: string) => (showNames ? n : maskName(n));
  const temFiltro = JSON.stringify(filtros) !== JSON.stringify(FILTROS_INICIAIS) || busca !== "";

  const limpar = () => { setFiltros(FILTROS_INICIAIS); setBusca(""); };

  const exportar = async () => {
    try {
      const blob = await packageSessionsService.exportXlsx(filtros);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `pacotes_agendados_desde_${data?.from ?? ""}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setErro("Não foi possível gerar a planilha.");
    }
  };

  const abrirRecibo = (paymentId: string) =>
    window.open(`${window.location.pathname}?page=payment-receipt&id=${paymentId}`, "_blank", "noopener");

  return (
    <Box className="c4u-print-page" sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Pacotes agendados</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Sessões de cada pacote, linha a linha, com o agendamento pai, o status e o pagamento.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} className="no-print">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField size="small" label="Pacotes com sessão a partir de" type="date" value={filtros.from || data?.from || ""} onChange={(e) => muda("from", e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }} />
                <TextField size="small" label="Até" type="date" value={filtros.to} onChange={(e) => muda("to", e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }} />
                <TextField size="small" label="Paciente" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar pelo nome" fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }} />
                <TextField select size="small" label="Profissional" value={filtros.professionalId} onChange={(e) => muda("professionalId", e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">Todos</MenuItem>
                  {(data?.professionals ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Serviço" value={filtros.serviceTypeId} onChange={(e) => muda("serviceTypeId", e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">Todos</MenuItem>
                  {(data?.services ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Convênio / plano" value={filtros.planKey} onChange={(e) => muda("planKey", e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">Todos</MenuItem>
                  {(data?.plans ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Origem do agrupamento" value={filtros.origin} onChange={(e) => muda("origin", e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">Todas</MenuItem>
                  {ORIGENS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Situação do pacote" value={filtros.packageSituation} onChange={(e) => muda("packageSituation", e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">Todas</MenuItem>
                  {SITUACOES.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Pagamento" value={filtros.paymentSituation} onChange={(e) => muda("paymentSituation", e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="Pago">Pago</MenuItem>
                  <MenuItem value="Pendente">Pendente</MenuItem>
                </TextField>
                <FormControlLabel
                  control={<Checkbox size="small" checked={filtros.includeExcluded} onChange={(e) => muda("includeExcluded", e.target.checked)} sx={{ color: colors.primary, "&.Mui-checked": { color: colors.primary } }} />}
                  label={<Typography sx={{ fontSize: 13 }}>Mostrar excluídas</Typography>}
                />
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={limpar} disabled={!temFiltro || loading} />
                <Button
                  variant="contained" size="small" onClick={() => setShowNames((v) => !v)}
                  startIcon={showNames ? <VisibilityOff /> : <Visibility />}
                  sx={{ textTransform: "none", backgroundColor: "#6c757d", "&:hover": { backgroundColor: "#5a6268" }, height: 40 }}
                >
                  {showNames ? "Ocultar nomes" : "Exibir nomes"}
                </Button>
                <Tooltip title="Imprimir" arrow>
                  <span>
                    <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={() => window.print()} disabled={loading || linhas.length === 0} aria-label="Imprimir"><Print /></IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Exportar para Excel" arrow>
                  <span>
                    <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={exportar} disabled={loading || linhas.length === 0} aria-label="Exportar"><FileDownload /></IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

            <Box className="c4u-info-box no-print">
              <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
              <Typography>
                Pacote é serviço com mais de 1 sessão configurada. Nas sessões vindas do sistema anterior o pacote é
                reconstruído: recibo que pagou o pacote, sessões agendadas juntas ("Agendado junto") e, para o resto, a
                sequência de datas — confira primeiro os de origem "Sequência". O período escolhe pacotes: o pacote entra
                inteiro, inclusive com sessões anteriores. Falta conta como sessão; excluída não conta e aparece em cinza.
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} sx={{ color: colors.primary }} />
              </Box>
            ) : erro ? (
              <Typography sx={{ textAlign: "center", color: "#dc3545", py: 4 }}>{erro}</Typography>
            ) : (
              <>
                <Box sx={summaryCardsRowSx} className="c4u-print-block">
                  <ReportSummaryCard label="Pacotes" value={String(data?.packages ?? 0)} sub={`${data?.patients ?? 0} pacientes`} />
                  <ReportSummaryCard label="Sessões ativas" value={String(data?.activeSessions ?? 0)} sub={`${data?.excludedSessions ?? 0} excluídas`} />
                  <ReportSummaryCard label="Previsto" value={moeda(data?.expected ?? 0)} />
                  <ReportSummaryCard label="Realizado (pago)" value={moeda(data?.realized ?? 0)} sub={`${data?.paidSessions ?? 0} sessões pagas`} subColor="#155724" />
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }} className="c4u-print-block">
                  <Bloco titulo="Por origem do agrupamento" grupos={data?.byOrigin ?? []} />
                  <Bloco titulo="Por situação do pacote" grupos={data?.bySituation ?? []} />
                </Box>

                <ResultCount shown={linhas.length} />

                <Box className="c4u-table">
                  <Box className="c4u-table__header">
                    <Box className="admin-plans-header-cell" sx={{ flex: "1 1 170px" }}>Paciente</Box>
                    <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "0 0 124px" }}>Cadastro</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 86px" }}>Data</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 56px" }}>Hora</Box>
                    <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 1 130px" }}>Pacote (pai)</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 76px" }}>Sessão</Box>
                    <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 1 120px" }}>Origem · situação</Box>
                    <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "1 1 170px" }}>Serviço · plano</Box>
                    <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "1 1 140px" }}>Profissional</Box>
                    <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 1 130px" }}>Status</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 92px", textAlign: "right" }}>Previsto</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 108px" }}>Pagamento</Box>
                    <Box className="admin-plans-header-cell admin-plans-actions no-print" sx={{ flex: "0 0 60px" }}>Recibo</Box>
                  </Box>

                  {daPagina.length === 0 ? (
                    <Typography sx={{ px: 2, py: 3, fontSize: 13, color: "#adb5bd", textAlign: "center" }}>
                      Nenhum pacote com sessão no período e nos filtros escolhidos.
                    </Typography>
                  ) : (
                    daPagina.map((r: PackageSessionRow) => {
                      const cor = situacaoCor[r.packageSituation];
                      const listrada = (faixa.get(r.parentId) ?? 0) % 2 === 1;
                      return (
                        <Box key={r.scheduleId} className="c4u-table__row"
                          sx={{ backgroundColor: listrada ? "#f8fbfb !important" : undefined, opacity: r.active ? 1 : 0.6, fontStyle: r.active ? "normal" : "italic" }}>
                          <Box className="admin-plans-cell" data-label="Paciente" sx={{ flex: "1 1 170px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, overflowWrap: "anywhere" }}>{nome(r.patientName)}</Typography>
                          </Box>
                          <Box className="admin-plans-cell c4u-col--xl" data-label="Cadastro" sx={{ flex: "0 0 124px", fontSize: 12 }}>{dataHoraBr(r.createdAt)}</Box>
                          <Box className="admin-plans-cell" data-label="Data" sx={{ flex: "0 0 86px" }}>
                            {dataBr(r.start)}
                            {r.beforePeriod && <Typography sx={{ fontSize: 10.5, color: "#6c757d" }}>antes do período</Typography>}
                          </Box>
                          <Box className="admin-plans-cell" data-label="Hora" sx={{ flex: "0 0 56px" }}>{hora(r.start)}</Box>
                          <Box className="admin-plans-cell c4u-col--lg" data-label="Pacote (pai)" sx={{ flex: "0 1 130px", minWidth: 0 }}>
                            <Tooltip title={r.parentId} arrow>
                              <Typography sx={{ fontSize: 11.5, fontFamily: "monospace", color: "#495057" }}>{r.parentId.slice(0, 8)}…</Typography>
                            </Tooltip>
                          </Box>
                          <Box className="admin-plans-cell" data-label="Sessão" sx={{ flex: "0 0 76px", fontWeight: 600 }}>{r.session}</Box>
                          <Box className="admin-plans-cell c4u-col--md" data-label="Origem · situação" sx={{ flex: "0 1 120px", display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                            <Typography sx={{ fontSize: 11.5, color: r.origin === "Sequência" ? "#b8860b" : "#495057", fontWeight: r.origin === "Sequência" ? 600 : 400 }}>{r.origin}</Typography>
                            {cor && <Chip size="small" label={r.packageSituation} sx={{ height: 20, fontSize: "0.68rem", fontWeight: 600, bgcolor: cor.bg, color: cor.fg }} />}
                          </Box>
                          <Box className="admin-plans-cell c4u-col--lg" data-label="Serviço · plano" sx={{ flex: "1 1 170px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12.5, overflowWrap: "anywhere" }}>{r.serviceName}</Typography>
                            <Typography sx={{ fontSize: 11, color: "#6c757d" }}>{r.planName}</Typography>
                          </Box>
                          <Box className="admin-plans-cell c4u-col--xl" data-label="Profissional" sx={{ flex: "1 1 140px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12.5, overflowWrap: "anywhere" }}>{nome(r.professionalName)}</Typography>
                          </Box>
                          <Box className="admin-plans-cell c4u-col--md" data-label="Status" sx={{ flex: "0 1 130px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 11.5 }}>{r.confirmation}</Typography>
                            <Typography sx={{ fontSize: 11.5, color: "#6c757d" }}>{r.presence}</Typography>
                          </Box>
                          <Box className="admin-plans-cell" data-label="Previsto" sx={{ flex: "0 0 92px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{moeda(r.expected)}</Box>
                          <Box className="admin-plans-cell" data-label="Pagamento" sx={{ flex: "0 0 108px" }}>
                            {!r.active ? (
                              <Typography sx={{ fontSize: 12, color: "#6c757d" }}>excluída</Typography>
                            ) : r.paid ? (
                              <>
                                <Chip size="small" label="Pago" sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600, bgcolor: colors.successSoft, color: "#155724" }} />
                                <Typography sx={{ fontSize: 11, color: "#495057" }}>{moeda(r.realized ?? 0)}{r.paymentDate ? ` · ${dataBr(r.paymentDate)}` : ""}</Typography>
                              </>
                            ) : (
                              <Chip size="small" label="Pendente" sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600, bgcolor: colors.warningSoft, color: colors.warningSoftText }} />
                            )}
                          </Box>
                          <Box className="admin-plans-cell admin-plans-actions no-print" sx={{ flex: "0 0 60px" }}>
                            {r.paymentId ? (
                              <Tooltip title="Abrir recibo" arrow>
                                <IconButton className="c4u-btn-icon" onClick={() => abrirRecibo(r.paymentId!)} sx={{ backgroundColor: colors.primary, "&:hover": { backgroundColor: "#029AAB" } }} aria-label="Abrir recibo">
                                  <Receipt sx={{ fontSize: "1rem", color: "#fff" }} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Typography sx={{ fontSize: 11, color: "#adb5bd" }}>—</Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>

                <Box className="no-print">
                  <StandardPagination
                    currentPage={pagina}
                    totalPages={Math.max(1, Math.ceil(linhas.length / porPagina))}
                    itemsPerPage={porPagina}
                    totalItems={linhas.length}
                    onPageChange={setPagina}
                    onItemsPerPageChange={(n) => { setPorPagina(n); setPagina(1); }}
                  />
                </Box>
              </>
            )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportPackageSessions;
